import { sleep } from "../animation/sleep";
import { App } from "../app/App";
import { ClassFlag } from "../dom-tools/ClassFlag";
import { ElementAttribute } from "../dom-tools/ElementAttribute";
import { ElementType } from "../dom-tools/ElementType";
import { OnClick } from "../dom-tools/EventListeners";
import { waitAround } from "../tools/sleep";

export interface VendorPrefixedFullscreenDocument extends Document
{
  mozFullScreenElement:Element;
  webkitFullscreenElement:Element;
  msFullscreenElement:Element;  

  mozCancelFullScreen():void;
  webkitExitFullscreen():void;
  msExitFullscreen():void;
}

export interface VendorPrefixedFullscreenElement extends Element
{
  mozRequestFullScreen():void;
  webkitRequestFullscreen():void;
  msRequestFullscreen():void;
}

export class Fullscreen
{
  static get element()
  {
    let vpDoc = document as VendorPrefixedFullscreenDocument;

    let fsElement = 
      vpDoc.fullscreenElement ||
      vpDoc.webkitFullscreenElement || 
      vpDoc.mozFullScreenElement ||
      vpDoc.msFullscreenElement;

    if ( fsElement === undefined )
    {
      fsElement = null;
    }

    return fsElement;
  }

  static get active()
  {
    return Fullscreen.isFullscreen();
  }

  static readonly inFullscreen = new ClassFlag( "in--fullscreen" );
  static readonly fullscreenchange = "fullscreenchange";

  static async toggle( element:Element )
  {
    if ( Fullscreen.active )
    {      
      await Fullscreen.exit();
      Fullscreen.inFullscreen.removeFrom( element );
    }
    else
    {
      try
      {
        // App.logInfo( "Requesting fullscreen" );
        await Fullscreen.request( element );
        // App.logInfo( "Entered fullscreen" );
      }
      catch ( exc )
      {
        // App.logError( "FAILED FS REQUEST:", exc );
      }
      
     
      Fullscreen.inFullscreen.set( element );

      let updateFlag = async ()=>
      {
        if ( Fullscreen.element )
        {
          return;
        }
        
        
        document.removeEventListener( Fullscreen.fullscreenchange, updateFlag );

        Fullscreen.inFullscreen.removeFrom( element );

      };

      document.addEventListener( Fullscreen.fullscreenchange, updateFlag );
    }
  }

  static async toggleFullscreen( videoContainer:HTMLElement, handleOrientation:"auto"|"portrait"|"landscape"=null ): Promise<void>
  {
    let videoElement = ElementType.video.query( videoContainer );
    ElementAttribute.playsinline.to( videoContainer );
    ( videoContainer as any ).playsInline = true;

    if ( Fullscreen.isFullscreen() )
    {
      await Fullscreen.exit();

      if ( handleOrientation )
      { 
        Fullscreen.unlockOrientation();
      }
      
      return Promise.resolve();
    }

    let target:string = null;

    if ( handleOrientation !== null )
    {
      if ( handleOrientation === "auto" )
      {
        let vw = videoElement.videoWidth || videoElement.clientWidth || 16;
        let vh = videoElement.videoHeight || videoElement.clientHeight || 9;

        target = vw >= vh ? "landscape" : "portrait";
      }
      else
      {
        target = handleOrientation;
      }
    }

    await Fullscreen.request( videoContainer );

    if ( target != null )
    {
      await Fullscreen.lockOrientation( target );
    }

    return Promise.resolve();
  }

  static isFullscreen(): boolean
  {
    let anyDocument:any = document;

    if ( anyDocument.fullscreenElement  )
    {
      return true;
    }

    if ( anyDocument.webkitFullscreenElement )
    {
      return true;
    }

    if ( anyDocument.mozFullScreenElement )
    {
      return true;
    }

    if ( anyDocument.msFullscreenElement )
    {
      return true;
    }

    return false;
  }

  // protected static async enterFullscreen( element:HTMLElement ): Promise<void>
  // {
  //   let anyElement:any = element;

  //   if ( element.requestFullscreen )
  //   {
  //     return element.requestFullscreen();
  //   }

  //   if ( anyElement.webkitRequestFullscreen )
  //   {
  //     return anyElement.webkitRequestFullscreen();
  //   }

  //   if ( anyElement.mozRequestFullScreen )
  //   {
  //     return anyElement.mozRequestFullScreen();
  //   }

  //   if ( anyElement.msRequestFullscreen )
  //   {
  //     return anyElement.msRequestFullscreen();
  //   }

  //   return Promise.reject( new Error( "fullscreen api not supported" ));
  // }

  // static async exitFullscreen(): Promise<void>
  // {
  //   let anyDocument: any = document;

  //   if ( anyDocument.exitFullscreen )
  //   {
  //     return anyDocument.exitFullscreen();
  //   }

  //   if ( anyDocument.webkitExitFullscreen )
  //   {
  //     return anyDocument.webkitExitFullscreen();
  //   }

  //   if ( anyDocument.mozCancelFullScreen )
  //   {
  //     return anyDocument.mozCancelFullScreen();
  //   }

  //   if ( anyDocument.msExitFullscreen )
  //   {
  //     return anyDocument.msExitFullscreen();
  //   }

  //   return Promise.reject( new Error( "fullscreen exit not supported" ));
  // }

  static async lockOrientation( orientation:string ): Promise<void>
  {
    let anyScreen: any = ( screen as any );
    let api: any = screen.orientation != null
                    ? screen.orientation
                    : anyScreen.orientation != null
                      ? anyScreen.orientation
                      : anyScreen.msOrientation;

    if ( api == null || typeof api.lock !== "function" )
    {
      return;
    }

    try
    {
      await api.lock( orientation );
    }
    catch
    {
      // fail quietly
    }
  }

  static unlockOrientation(): void
  {
    let anyScreen: any = ( screen as any );
    let api: any = screen.orientation != null
                    ? screen.orientation
                    : anyScreen.orientation != null
                      ? anyScreen.orientation
                      : anyScreen.msOrientation;

    if ( api == null || typeof api.unlock !== "function" )
    {
      return;
    }

    try
    {
      api.unlock();
    }
    catch
    {
      // fail quietly
    }
  }

  static async request( element:Element, navigationUI:"auto"|"show"|"hide" = "auto" ):Promise<void>
  {

    // App.logInfo( "Request FS:", element, element == null, element == null ? "null" : element.nodeName );

    let vpElement = element as VendorPrefixedFullscreenElement;

    if ( vpElement.requestFullscreen ) 
    { 
      // App.logInfo( vpElement, "Requested standard FS" );
      // let options:FullscreenOptions = { navigationUI: navigationUI }; 
      await vpElement.requestFullscreen(); 
    } 
    else if ( vpElement.mozRequestFullScreen ) 
    { 
      // App.logInfo( "Requested moz FS" );
      await vpElement.mozRequestFullScreen(); 
    } 
    else if ( vpElement.webkitRequestFullscreen ) 
    { 
      // App.logInfo( "Requested webkit FS" );
      await vpElement.webkitRequestFullscreen(); 
    } 
    else if ( vpElement.msRequestFullscreen ) 
    { 
      // App.logInfo( "Requested ms FS" );
      await vpElement.msRequestFullscreen(); 
    }

    // App.logInfo( "Awaited FS request" );
    
    return Promise.resolve();
  }

  static async exit():Promise<void>
  {
    let vpDoc = document as VendorPrefixedFullscreenDocument;

    if ( vpDoc.exitFullscreen) 
    { 
      await vpDoc.exitFullscreen(); 
    } 
    else if ( vpDoc.webkitExitFullscreen ) 
    { 
      await vpDoc.webkitExitFullscreen(); 
    } 
    else if ( vpDoc.mozCancelFullScreen ) 
    { 
      await vpDoc.mozCancelFullScreen(); 
    } 
    else if ( vpDoc.msExitFullscreen ) 
    { 
      await vpDoc.msExitFullscreen(); 
    }

    return Promise.resolve();
  }
}